/**
 * TableScreen for Poker Reborn
 *
 * Main poker table view with seats, cards, and game controls.
 * The Room where the magic happens!
 *
 * @see docs/screens/room/README.md - Component map (start here!)
 * @see docs/screens/room/TABLE.md - Table layout, seat positions
 * @see docs/screens/room/GAME_FLOW.md - Hand progression, betting rounds
 */

import { Container, FillGradient, Graphics, Point, Text } from 'pixi.js';
import { TableView } from '@/components/table/TableView';
import { TableInfoView } from '@/components/table/TableInfoView';
import { SeatView } from '@/components/table/SeatView';
import { TablePotView } from '@/components/table/TablePotView';
import { PlayerPotView } from '@/components/table/PlayerPotView';
import { DealerButtonView } from '@/components/table/DealerButtonView';
import { CommunityCardsView } from '@/components/table/CommunityCardsView';
import { HandNameView } from '@/components/table/HandNameView';
import { HeroView } from '@/components/table/HeroView';
import { RoomBar } from '@/components/table/RoomBar';
import { HoleCardsView } from '@/components/cards/HoleCardsView';
import { GamePadView } from '@/components/gamepad/GamePadView';
import { BuyInPopup } from '@/components/popups/BuyInPopup';
import { BuyinErrorPopup } from '@/components/popups/BuyinErrorPopup';
import { LeaveWarningPopup } from '@/components/popups/LeaveWarningPopup';
import { SitoutWarningPopup } from '@/components/popups/SitoutWarningPopup';
import { RebuyPopup } from '@/components/popups/RebuyPopup';
import { ProfilePopup } from '@/components/popups/ProfilePopup';
import { MyProfilePopup } from '@/components/popups/MyProfilePopup';
import { GiftPopup } from '@/components/popups/GiftPopup';
import { tableState } from '@/state/TableState';

import { tableController } from '@/controllers/TableController';
import { accountService } from '@/services/AccountService';
import { gameController } from '@/controllers/GameController';
import { log } from '@/utils/log';
import { targetRegistry } from '@/effects/registry';
import { stageManager } from '@/effects/stage';
import { gameEventBus } from '@/effects/events/GameEventBus';
import { giftService } from '@/services/GiftService';
import { GIFT_CATALOG } from '@/config/gifts';
import { DealerButtonMoveEffect } from '@/effects/library/table/DealerButtonMoveEffect';
import {
  SEAT_POSITIONS,
  DEALER_BUTTON_POSITION,
  POT_POSITION,
  COMMUNITY_CARDS,
  PLAYER_POT_POSITION,
  HOLE_CARDS_POSITION,
  TABLE_POSITION,
  TABLE_INFO_POSITION,
  getRotatedSeatPosition,
  getRotatedOffset,
  getVisualPositionIndex,
} from '@/config/layout';
import { DEALING_CONFIG } from '@/config/dealingAnimation';
import { layoutService } from '@/services/LayoutService';
// Import to initialize the event collector (singleton auto-subscribes on first import)
import { gameStartEventCollector } from '@/effects/sequencer';
import type { SequencerBlindsEvent } from '@/effects/events/GameEvents';
import { ChipBetEffect } from '@/effects/library/chips/ChipBetEffect';
import { BLIND_ANIMATION_TIMING, STREET_END_TIMING } from '@/config/animation';
import { audioServiceAdapter } from '@/effects/library/sound/AudioServiceAdapter';
import { getScreenManager } from './ScreenManager';
import { enableLayoutEditor } from '@/utils/layoutEditor';
import type { TableSize, PlayerState, Card } from '@/types/game';

// =============================================================================
// TABLE SCREEN
// =============================================================================

export class TableScreen extends Container {
  private screenWidth: number;
  private screenHeight: number;

  // Layers (Z-order: 0=background, 1=table, 2=seats, 3=playersCards, 4=playerPots, 5=dealerButton, 6=tablePot, 7=communityCards, 8=particles, 9=ui, 10=overlays, 11=popup)
  private backgroundLayer: Container;
  private tableLayer: Container;
  private seatsLayer: Container;
  private playersCardsLayer: Container;
  private playerPotsLayer: Container;
  private dealerButtonLayer: Container;
  private tablePotLayer: Container;
  private communityCardsLayer: Container;
  private particleLayer: Container;
  private uiLayer: Container;
  private overlayEffectsLayer: Container;
  private popupLayer: Container;

  // Components
  private background: Graphics;
  private roomBar: RoomBar;
  private tableView: TableView;
  private tableInfoView: TableInfoView;
  private seatViews: Map<number, SeatView> = new Map();
  private playerPotViews: Map<number, PlayerPotView> = new Map();
  private tablePotView: TablePotView;
  private dealerButton: DealerButtonView;
  private communityCards: CommunityCardsView;
  private handNameView: HandNameView;
  private heroView: HeroView;
  private holeCardsViews: Map<number, HoleCardsView> = new Map();
  /** Original HoleCardsView from createSeats for the hero seat (stored when swapped) */
  private originalHeroHoleCards: HoleCardsView | null = null;
  /** Currently active hero seat number (tracked for swap restore) */
  private activeHeroSeat: number | null = null;
  private gamePad: GamePadView;
  private flyingCardsContainer: Container;

  // State
  private tableSize: TableSize = 9;
  private layoutEditorEnabled = false;
  /** True from street start until any player acts on that street */
  private isNewStreet = false;
  private buyInPopup: BuyInPopup | null = null;
  private buyinErrorPopup: BuyinErrorPopup | null = null;
  private leaveWarningPopup: LeaveWarningPopup | null = null;
  private sitoutWarningPopup: SitoutWarningPopup | null = null;
  private rebuyPopup: RebuyPopup | null = null;
  private profilePopup: ProfilePopup | null = null;
  private myProfilePopup: MyProfilePopup | null = null;
  private giftPopup: GiftPopup | null = null;

  constructor(width: number, height: number) {
    super();
    this.screenWidth = width;
    this.screenHeight = height;

    // Create layers (Z-order from bottom to top)
    this.backgroundLayer = new Container();
    this.backgroundLayer.label = 'backgroundLayer';

    this.tableLayer = new Container();
    this.tableLayer.label = 'tableLayer';

    this.seatsLayer = new Container();
    this.seatsLayer.label = 'seatsLayer';

    this.playersCardsLayer = new Container();
    this.playersCardsLayer.label = 'playersCardsLayer';

    this.playerPotsLayer = new Container();
    this.playerPotsLayer.label = 'playerPotsLayer';

    this.dealerButtonLayer = new Container();
    this.dealerButtonLayer.label = 'dealerButtonLayer';

    this.tablePotLayer = new Container();
    this.tablePotLayer.label = 'tablePotLayer';

    this.communityCardsLayer = new Container();
    this.communityCardsLayer.label = 'communityCardsLayer';

    this.particleLayer = new Container();
    this.particleLayer.label = 'particleLayer';

    this.uiLayer = new Container();
    this.uiLayer.label = 'uiLayer';

    this.overlayEffectsLayer = new Container();
    this.overlayEffectsLayer.label = 'overlayEffectsLayer';

    this.popupLayer = new Container();
    this.popupLayer.label = 'popupLayer';

    // Add layers in Z-order (0=bottom, 11=top)
    this.addChild(this.backgroundLayer);
    this.addChild(this.tableLayer);
    this.addChild(this.seatsLayer);
    this.addChild(this.playersCardsLayer);
    this.addChild(this.playerPotsLayer);
    this.addChild(this.dealerButtonLayer);
    this.addChild(this.communityCardsLayer);
    this.addChild(this.tablePotLayer);
    this.addChild(this.particleLayer);
    // Register particleLayer immediately - it doesn't depend on positions
    targetRegistry.registerParticleLayer(this.particleLayer);
    this.addChild(this.uiLayer);
    this.addChild(this.overlayEffectsLayer);
    this.addChild(this.popupLayer);

    // Create background (violet casino gradient)
    this.background = new Graphics();
    this.backgroundLayer.addChild(this.background);

    // Create RoomBar (top navigation with balance, title, status label)
    this.roomBar = new RoomBar(width, height);
    this.roomBar.onLeave = () => { this.handleLeaveRequest(); };
    this.roomBar.onSitOut = () => { this.handleSitoutRequest(); };
    this.roomBar.onBack = () => { tableController.requestBack(); };
    this.uiLayer.addChild(this.roomBar);

    // Create table
    this.tableView = new TableView(width, height);
    this.tableLayer.addChild(this.tableView);

    // Create table info label (game type + blinds on felt)
    this.tableInfoView = new TableInfoView();
    this.tableLayer.addChild(this.tableInfoView);

    // Create table pot view (center pot display)
    this.tablePotView = new TablePotView();
    this.tablePotLayer.addChild(this.tablePotView);

    // Create community cards (on communityCardsLayer)
    this.communityCards = new CommunityCardsView();
    this.communityCardsLayer.addChild(this.communityCards);

    // Create hand name display (above community cards, same layer)
    this.handNameView = new HandNameView();
    this.communityCardsLayer.addChild(this.handNameView);

    // Create hero seat view (split container: seat visuals + hero cards + hand meter)
    this.heroView = new HeroView();
    this.heroView.setCommunityCardsView(this.communityCards); // T71: Wire draw prediction ref
    this.seatsLayer.addChild(this.heroView.seatContainer);
    this.playersCardsLayer.addChild(this.heroView.cardsContainer);

    // Create dealer button (on its own layer above seats)
    this.dealerButton = new DealerButtonView();
    this.dealerButtonLayer.addChild(this.dealerButton);

    // Hole cards are created in createSeats() for all seats

    // Create flying cards container (for card deal animations)
    this.flyingCardsContainer = new Container();
    this.flyingCardsContainer.label = 'flyingCardsContainer';
    this.communityCardsLayer.addChild(this.flyingCardsContainer);

    // Create game pad (action buttons)
    this.gamePad = new GamePadView(width, height);
    this.gamePad.onAction = (action, amount) => {
      log.table.debug`TableScreen.onAction received: ${action}${amount !== undefined ? ` ${amount}` : ''}`;
      if (action === 'fold') gameController.fold();
      else if (action === 'check') gameController.check();
      else if (action === 'call') gameController.call();
      else if (action === 'bet' && amount) gameController.bet(amount);
      else if (action === 'raise' && amount) gameController.raise(amount);
      else if (action === 'allin') gameController.allIn();
    };
    this.gamePad.onShowdownAction = (action) => {
      log.table.info`TableScreen.onShowdownAction: ${action}`;
      if (action === 'show') gameController.show();
      else if (action === 'muck') gameController.muck();
    };
    this.uiLayer.addChild(this.gamePad);

    // Wire pre-action callbacks for GameController
    gameController.onCheckPreAction = () => this.gamePad.getQueuedPreAction();
    gameController.onClearPreAction = () => { this.gamePad.clearQueuedPreAction(); };

    // Setup subscriptions
    this.setupSubscriptions();
    this.setupGameSubscriptions();

    // Register table operations with StageManager for sequencers
    this.registerTableOperations();

    // DEBUG: Keyboard shortcut to test RebuyPopup (Shift+R)
    // TODO: Remove before production
    this.setupDebugKeys();

    // Initial layout
    this.layout();
  }

  /**
   * DEBUG: Setup keyboard shortcuts for testing popups
   * TODO: Remove before production
   *
   * @see docs/dev/DEBUG_SHORTCUTS.md - Full documentation of debug shortcuts
   */
  private setupDebugKeys(): void {
    window.addEventListener('keydown', (e) => {
      // Shift+R = Show RebuyPopup with test values
      if (e.shiftKey && e.key === 'R') {
        log.table.info`DEBUG: Triggering test RebuyPopup`;
        this.showRebuyPopup(
          40,   // minBuyIn
          200,  // maxBuyIn
          60,   // timeout (seconds)
          'end' // at
        );
      }

      // Shift+G = Trigger mock gift between two occupied seats
      if (e.shiftKey && e.key === 'G') {
        this.triggerDebugGift();
      }

      // Shift+C = Show ConnectionOverlay (Reconnecting state) with test values
      if (e.shiftKey && e.key === 'C') {
        log.table.info`DEBUG: Triggering test ConnectionOverlay (Reconnecting)`;
        const screenManager = getScreenManager();
        if (screenManager) {
          const connectionOverlay = screenManager.getConnectionOverlay();
          connectionOverlay.showReconnecting(1, 5000, () => {
            log.table.info`DEBUG: "Try Now" button clicked`;
          }); // Attempt 1, 5s countdown, with Try Now button
        }
      }
    });
  }

  /**
   * Initialize layout editor (dev tool).
   * Enables visual drag-and-drop positioning of UI elements.
   * Activated via URL parameter: ?layout-edit=true
   *
   * @see tasks/active/T21_LAYOUT_EDITOR_PLAN.md
   */
  private initLayoutEditor(): void {
    // Only initialize once
    if (this.layoutEditorEnabled) return;

    // Only in dev mode with URL parameter
    if (!import.meta.env.DEV) return;
    if (!new URLSearchParams(window.location.search).has('layout-edit')) return;

    this.layoutEditorEnabled = true;

    // Collect all draggable elements
    const elements: Array<{ id: string; container: Container }> = [];

    // Register table (center position)
    elements.push({ id: 'table', container: this.tableView });

    // Register seats
    this.seatViews.forEach((seatView, seat) => {
      elements.push({ id: `seat_${seat}`, container: seatView });
    });

    // Register player pots
    this.playerPotViews.forEach((potView, seat) => {
      elements.push({ id: `pot_${seat}`, container: potView });
    });

    // Register hole cards (all seats)
    this.holeCardsViews.forEach((cardsView, seat) => {
      elements.push({ id: `cards_${seat}`, container: cardsView });
    });

    // Enable layout editing
    enableLayoutEditor(elements, this.tableSize, {
      width: this.screenWidth,
      height: this.screenHeight,
    });
  }

  /**
   * Resize the screen
   */
  resize(width: number, height: number): void {
    // Check global rotation state and swap dimensions if rotated 90°
    const screenManager = getScreenManager();
    const rotation = screenManager?.getRotation() ?? 0;

    if (rotation === 90) {
      // Swap: use height as width, width as height for layout calculations
      [width, height] = [height, width];
    }

    this.screenWidth = width;
    this.screenHeight = height;

    this.tableView.resize(width, height);
    // Apply TABLE_POSITION offset (0.5, 0.5 = centered, no change)
    this.tableView.x = (TABLE_POSITION.x - 0.5) * width;
    this.tableView.y = (TABLE_POSITION.y - 0.5) * height;
    this.tableInfoView.resize(width);
    this.roomBar.resize(width, height);
    this.gamePad.resize(width, height);
    this.dealerButton.resize(width);
    this.tablePotView.resize(width);
    this.layout();
    this.updateSeats();
    this.updatePlayerPots();
    this.updateHoleCardsPositions();
    this.registerEffectTargets();

    // Update dealer button position (no animation on resize)
    const dealerSeat = tableState.dealerSeat.get();
    if (dealerSeat >= 0) {
      this.updateDealerButton(dealerSeat, false);
    }

    // Resize active popups
    if (this.buyInPopup) {
      this.buyInPopup.resize(width, height);
    }
    if (this.buyinErrorPopup) {
      this.buyinErrorPopup.resize(width, height);
    }
    if (this.rebuyPopup) {
      this.rebuyPopup.resize(width, height);
    }
    if (this.leaveWarningPopup) {
      this.leaveWarningPopup.resize(width, height);
    }
    if (this.sitoutWarningPopup) {
      this.sitoutWarningPopup.resize(width, height);
    }
    if (this.profilePopup) {
      this.profilePopup.resize(width, height);
    }
    if (this.myProfilePopup) {
      this.myProfilePopup.resize(width, height);
    }
    if (this.giftPopup) {
      this.giftPopup.resize(width, height);
    }
  }

  // ===========================================================================
  // PUBLIC LAYER ACCESSORS (for Effects System)
  // ===========================================================================

  /** Get background layer (Z-Index 0) */
  getBackgroundLayer(): Container {
    return this.backgroundLayer;
  }

  /** Get table layer (Z-Index 1) */
  getTableLayer(): Container {
    return this.tableLayer;
  }

  /** Get seats layer (Z-Index 2) */
  getSeatsLayer(): Container {
    return this.seatsLayer;
  }

  /** Get players cards layer (Z-Index 3) - all hole cards (hero + opponents) */
  getPlayersCardsLayer(): Container {
    return this.playersCardsLayer;
  }

  /** Get player pots layer (Z-Index 4) */
  getPlayerPotsLayer(): Container {
    return this.playerPotsLayer;
  }

  /** Get dealer button layer (Z-Index 5) */
  getDealerButtonLayer(): Container {
    return this.dealerButtonLayer;
  }

  /** Get table pot layer (Z-Index 6) */
  getTablePotLayer(): Container {
    return this.tablePotLayer;
  }

  /** Get community cards layer (Z-Index 7) - board cards + flying cards */
  getCommunityCardsLayer(): Container {
    return this.communityCardsLayer;
  }

  /** Get particle layer for effects (Z-Index 8) - confetti, coins, sparkles */
  getParticleLayer(): Container {
    return this.particleLayer;
  }

  /** Get UI layer (Z-Index 9) */
  getUiLayer(): Container {
    return this.uiLayer;
  }

  /** Get overlay effects layer (Z-Index 10) - screen flash, vignette, blur */
  getOverlayEffectsLayer(): Container {
    return this.overlayEffectsLayer;
  }

  /** Get popup layer (Z-Index 11) */
  getPopupLayer(): Container {
    return this.popupLayer;
  }

  /** @deprecated Use getCommunityCardsLayer() instead */
  getCardsLayer(): Container {
    return this.communityCardsLayer;
  }

  /** Get seat view by seat number */
  getSeatView(seatNumber: number): SeatView | undefined {
    return this.seatViews.get(seatNumber);
  }

  /** Get all seat views */
  getSeatViews(): Map<number, SeatView> {
    return this.seatViews;
  }

  /** Get table pot view */
  getTablePotView(): TablePotView {
    return this.tablePotView;
  }

  /** Get player pot view by seat number */
  getPlayerPotView(seatNumber: number): PlayerPotView | undefined {
    return this.playerPotViews.get(seatNumber);
  }

  /** Get all player pot views */
  getPlayerPotViews(): Map<number, PlayerPotView> {
    return this.playerPotViews;
  }

  /** @deprecated Use getTablePotView() instead */
  getPotView(): TablePotView {
    return this.tablePotView;
  }

  /** Get community cards view */
  getCommunityCardsView(): CommunityCardsView {
    return this.communityCards;
  }

  /** Get hero cards view */
  getHeroCardsView(): HoleCardsView | undefined {
    const heroSeat = tableState.heroSeat.get();
    if (heroSeat === null) return undefined;
    // Map entry is swapped to HeroView's HoleCardsView when hero is seated
    return this.holeCardsViews.get(heroSeat);
  }

  /** Get the hero seat view (dedicated cockpit component) */
  getHeroView(): HeroView {
    return this.heroView;
  }

  /** Get hole cards view by seat number */
  getHoleCardsView(seatNumber: number): HoleCardsView | undefined {
    return this.holeCardsViews.get(seatNumber);
  }

  /** Get all hole cards views */
  getHoleCardsViews(): Map<number, HoleCardsView> {
    return this.holeCardsViews;
  }

  /** Get dealer button view */
  getDealerButtonView(): DealerButtonView {
    return this.dealerButton;
  }

  /** Get flying cards container (for card deal animations) */
  getFlyingCardsContainer(): Container {
    return this.flyingCardsContainer;
  }

  /**
   * Refresh screen after tab visibility change.
   * Re-syncs visual state with game state to fix rendering issues
   * that occur when browser tab returns from background.
   *
   * Handles two issues:
   * 1. Cards disappearing - game continues in background but animations don't run
   * 2. Partial card rendering - GPU context loss causes graphics to be incomplete
   *
   * @see docs/systems/BACKGROUND_TAB_RECOVERY.md
   */
  refresh(): void {
    log.table.debug`TableScreen: refresh - re-syncing visual state`;

    const players = tableState.players.get();
    const heroSeat = tableState.heroSeat.get();

    // Step 1: Force redraw of all card graphics (fixes partial rendering after GPU context loss)
    this.holeCardsViews.forEach((holeCardsView) => {
      holeCardsView.redraw();
    });

    // Step 2: Re-sync opponent card views with game state
    this.holeCardsViews.forEach((holeCardsView, seat) => {
      if (seat === heroSeat) return;

      const player = players.get(seat);
      const viewState = holeCardsView.getState();
      const hasCardsInView = viewState.card1 !== null || viewState.card2 !== null;
      const isRevealed = viewState.card1FaceUp || viewState.card2FaceUp;
      const playerIsActive = player && (player.status === 'active' || player.status === 'allin');

      // Case 1: View has revealed cards (showdown) - re-show them revealed
      if (hasCardsInView && isRevealed) {
        holeCardsView.visible = true;
        const cards: [Card, Card] = [viewState.card1!, viewState.card2!];
        holeCardsView.setCards(cards, true);
      }
      // Case 2: Player has cards in game state - show backs
      else if (player?.hasCards && player.status !== 'folded') {
        holeCardsView.showBacks();
      }
      // Case 3: View was visible AND player is active (fallback for watchers)
      else if (holeCardsView.visible && playerIsActive) {
        holeCardsView.showBacks();
      }
    });

    // Step 3: Refresh hero cards if present (uses HeroView's cards via swapped map)
    if (heroSeat !== null) {
      const heroCardsView = this.heroView.getHoleCardsView();
      const heroCards = tableState.heroCards.get();
      if (heroCards) {
        heroCardsView.setCards(heroCards, true);
      }
    }

    // Step 4: Force position recalculation (updateSeats positions hero cockpit)
    this.updateSeats();
    this.updateHoleCardsPositions();

    // Step 5: Refresh community cards (only if not currently animating)
    // T36: Skip if animation in progress to avoid race condition
    const communityCards = tableState.communityCards.get();
    const isAnimating = this.communityCards.isAnimationInProgress();
    console.log(`[TableScreen] refresh: communityCards=${communityCards.length}, isAnimating=${isAnimating}`);
    if (communityCards.length > 0 && !isAnimating) {
      this.communityCards.setCards(communityCards);
    } else if (isAnimating) {
      console.log('[TableScreen] refresh: SKIPPED setCards (animation in progress)');
    }
  }

  /**
   * Nuclear reset - restore ALL card properties to pristine state.
   * Called at hand start to ensure clean slate before dealing.
   *
   * Resets:
   * - All hole cards (hero + opponents)
   * - Community cards
   */
  resetAllCards(): void {
    log.table.debug`TableScreen: resetAllCards - nuclear reset`;

    // Reset all hole cards views
    this.holeCardsViews.forEach((holeCardsView, _seat) => {
      holeCardsView.resetAllCards();
    });

    // Reset community cards
    this.communityCards.resetAllCards();

    // Hide hand name
    this.handNameView.hide();
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  /**
   * Register table operations with StageManager.
   * Called once during setup. GameStartSequencer uses these to trigger
   * table reset and dealing without going through events.
   *
   * @see tasks/active/T35_SIMPLIFIED_PHASE5_PLAN.md - Task 6
   */
  private registerTableOperations(): void {
    stageManager.registerTableOperations({
      resetTable: () => {
        // NUCLEAR RESET: Clean slate for all cards before dealing
        this.resetAllCards();
        // Hide showdown buttons from previous hand
        this.gamePad.hideShowMuckButtons();
        log.game.debug`TableScreen: resetTable via StageManager`;
      },
      startDealing: (dealerSeat: number) => {
        this.startDealingSequence(dealerSeat);
        log.game.debug`TableScreen: startDealing via StageManager (dealer seat ${dealerSeat})`;
      },
    });
    log.table.debug`TableScreen: registered table operations with StageManager`;
  }

  private setupSubscriptions(): void {
    // Subscribe to table state changes
    tableState.tableSize.subscribe((size) => {
      this.tableSize = size;
      this.createSeats();
      this.updateSeats();
    });

    // Subscribe to game info for table info label (game type + blinds)
    tableState.gameInfo.subscribe((info) => {
      if (info) {
        this.tableInfoView.setGameInfo(info.gameType, info.stakes);
        this.tableInfoView.visible = true;
      } else {
        this.tableInfoView.visible = false;
      }
    });

    // Track recovery state for community cards
    // These must be defined BEFORE tableId subscription so they can be reset on table join
    let prevCardCount = 0;
    let isInitialLoad = true; // First non-empty callback is state recovery, not animation

    // Show croupier GLID in status label + reset recovery state on table join
    tableState.tableId.subscribe((id) => {
      this.roomBar.setStatusLabel(id ?? '');

      // T36: When joining a new table, reset recovery state
      // This ensures first community cards use recovery path (immediate display)
      if (id !== null) {
        console.log('[TableScreen] tableId changed - resetting isInitialLoad=true');
        isInitialLoad = true;
        prevCardCount = 0;
      }
    });

    tableState.players.subscribe(() => {
      this.updateSeats();
    });

    tableState.dealerSeat.subscribe((seat) => {
      this.updateDealerButton(seat);
    });

    // NOTE: activeSeat subscription REMOVED - now handled by effects pipeline
    // player:highlight/player:unhighlight effects call SeatView.setActive()
    // via turn:started/turn:ended events

    tableState.pots.subscribe((pots) => {
      this.tablePotView.setPots(pots);
    });

    // Subscribe to player bets for PlayerPotViews
    tableState.players.subscribe((players) => {
      players.forEach((player, seat) => {
        const playerPotView = this.playerPotViews.get(seat);
        if (playerPotView) {
          playerPotView.setBet(player.bet);
        }
      });
      // Clear bets for empty seats
      this.playerPotViews.forEach((view, seat) => {
        if (!players.has(seat)) {
          view.setBet(0);
        }
      });
    });

    // Subscribe to players for hole cards display (state recovery)
    // This handles showing card backs for opponents when rejoining mid-hand
    // NOTE: Card reveals are NOT handled here - they go through showdown:reveal
    // event which is emitted by GameEndSequencer during END-2 for proper timing.
    tableState.players.subscribe((players) => {
      const heroSeat = tableState.heroSeat.get();

      players.forEach((player, seat) => {
        const holeCardsView = this.holeCardsViews.get(seat);
        if (!holeCardsView) return;

        // Hero's cards are shown by heroCards subscription, but we handle fold state here
        // (because players subscription fires after setPlayers, giving us correct status)
        if (seat === heroSeat) {
          if (player.status === 'folded' && tableState.heroCards.get()) {
            holeCardsView.fold();
          }
          return;
        }

        // Show card backs for opponents who have cards (but NOT face-up)
        // Face-up reveals are handled by showdown:reveal event from GameEndSequencer
        // NOTE: Fold animation is handled by player:fold event, not here
        if (player.hasCards && !holeCardsView.isRevealed() && player.status !== 'folded') {
          holeCardsView.showBacks();
        }
      });

      // Hide cards for empty seats
      this.holeCardsViews.forEach((view, seat) => {
        if (!players.has(seat) && seat !== heroSeat) {
          view.hide();
        }
      });
    });

    // Reset isInitialLoad when new hand starts (not recovery)
    // This ensures flop/turn/river use animation path instead of recovery path
    gameEventBus.on('hand:start', () => {
      console.log('[TableScreen] hand:start received - setting isInitialLoad=false');
      isInitialLoad = false;
    });

    tableState.communityCards.subscribe((cards) => {
      const currentCount = cards.length;
      console.log(`[TableScreen] communityCards subscription: count=${currentCount}, prevCount=${prevCardCount}, isInitialLoad=${isInitialLoad}`);

      if (currentCount === 0) {
        // Clear all cards (new hand starting)
        // Don't change isInitialLoad here - wait for actual cards
        console.log('[TableScreen] Clearing community cards (count=0)');
        this.communityCards.clear();
        prevCardCount = 0;
        return;
      }

      if (isInitialLoad) {
        // RECOVERY: Page refresh mid-game, show cards immediately (no animation)
        console.log('[TableScreen] RECOVERY: Setting cards immediately (isInitialLoad=true)');
        this.communityCards.setCards(cards);
        prevCardCount = currentCount;
        isInitialLoad = false;
        return;
      }

      // Normal game flow - use animations
      if (currentCount === 3 && prevCardCount < 3) {
        // FLOP: Prepare cards (hidden), StreetEndSequencer will animate
        this.communityCards.prepareFlopCards(cards as [Card, Card, Card]);
      } else if (currentCount === 4 && prevCardCount === 3) {
        // TURN: Prepare card (hidden), StreetEndSequencer will animate
        const turnCard = cards[3];
        if (turnCard) {
          this.communityCards.prepareTurnCard(turnCard);
        }
      } else if (currentCount === 5 && prevCardCount === 4) {
        // RIVER: Prepare card (hidden), StreetEndSequencer will animate
        const riverCard = cards[4];
        if (riverCard) {
          this.communityCards.prepareRiverCard(riverCard);
        }
      } else {
        // Fallback: Direct set (e.g., other edge cases)
        this.communityCards.setCards(cards);
      }

      prevCardCount = currentCount;
    });

    // Subscribe to controller events
    tableController.on('buyInRequested', ({ seat, minBuyIn, maxBuyIn }) => {
      this.showBuyInPopup(seat, minBuyIn, maxBuyIn);
    });

    tableController.on('buyinError', ({ message }) => {
      this.showBuyinErrorPopup(message);
    });

    // Gift error toast (T42)
    giftService.on('giftError', ({ message }) => {
      this.showErrorToast(message);
    });

    tableController.on('rebuyRequested', ({ minBuyIn, maxBuyIn, timeout, at }) => {
      this.showRebuyPopup(minBuyIn, maxBuyIn, timeout, at);
    });

    // Subscribe to hero seat changes to update UI
    tableState.heroSeat.subscribe((seat) => {
      const isSeated = seat !== null;
      this.roomBar.setSeated(isSeated);

      // Manage HeroView activation (swap map entries for hero cards)
      this.activateHeroSeat(seat);

      // Full re-layout with hero rotation applied
      this.layout(); // Ensure positions are updated before registering targets
      this.updateSeats();
      this.updatePlayerPots();
      this.updateHoleCardsPositions();
      this.registerEffectTargets();

      // Update dealer button position with rotation (no animation on hero rotation)
      const dealerSeat = tableState.dealerSeat.get();
      if (dealerSeat >= 0) {
        this.updateDealerButton(dealerSeat, false);
      }
    });

    // Subscribe to hero sitout state to toggle button label
    tableState.heroSitout.subscribe((isSitout) => {
      this.roomBar.setSitOut(isSitout);
    });

    // Subscribe to sitout countdown timers
    tableState.sitoutTimers.subscribe((timers) => {
      this.updateSitoutCountdowns(timers);
    });

    // Subscribe to layout changes for responsive sizing
    layoutService.on('resize', (layoutMode) => {
      // Resize seat views whenever screen dimensions change
      this.resizeSeatViews(layoutMode.screenWidth);
    });
  }

  private setupGameSubscriptions(): void {
    // Subscribe to hero cards
    tableState.heroCards.subscribe((cards) => {
      const heroSeat = tableState.heroSeat.get();
      if (heroSeat === null) return;

      const heroCardsView = this.holeCardsViews.get(heroSeat);
      if (!heroCardsView) return;

      if (!cards) {
        heroCardsView.hide();
      } else {
        // Show cards immediately (for session recovery)
        // Animation is handled by 'holeCardsDealt' event during normal play
        heroCardsView.setCards(cards, true);

        // Check if hero is folded (for state recovery after refresh)
        const heroPlayer = tableState.players.get().get(heroSeat);
        if (heroPlayer?.status === 'folded') {
          heroCardsView.fold();
        }
      }
    });

    // Subscribe to hero actions (delay is handled in GameController)
    tableState.heroActions.subscribe((actions) => {
      if (actions) {
        const totalPot = tableState.totalPot.get();
        this.gamePad.showActions(actions, totalPot);
      } else {
        this.gamePad.hideActions();
        // Hero acted — street is no longer "new"
        this.isNewStreet = false;
        this.updatePreActionCheckboxes();
      }
    });

    // NOTE: Hand Strength Meter subscriptions are now handled by HeroView internally

    gameController.on('timerTick', ({ seat, remaining, total }) => {
      if (seat === this.activeHeroSeat) {
        this.heroView.setTimerProgress(remaining / total);
      } else {
        const seatView = this.seatViews.get(seat);
        if (seatView) {
          seatView.setTimerProgress(remaining / total);
        }
      }
    });

    gameController.on('playerActed', ({ seat }) => {
      // Clear timer when player acts
      if (seat === this.activeHeroSeat) {
        this.heroView.setActive(false);
      } else {
        const seatView = this.seatViews.get(seat);
        if (seatView) {
          seatView.setActive(false);
        }
      }
    });

    gameController.on('handEnded', () => {
      // NOTE: Do NOT hide actions, cards, or reset highlights here!
      // Reason: handEnded fires AFTER showdown display, but we want to keep
      // the highlighted/dimmed card state visible until the next hand starts.
      //
      // Actions are already hidden by heroActions subscription when set to null.
      // Cards are replaced by dealBacks() when new hand starts.
      // Highlights are reset by showBacks()/dealBacks() when new cards are dealt.
    });

    // Showdown prompt - hero can show or muck cards
    gameController.on('showdownPrompt', () => {
      this.gamePad.showShowMuckButtons();
    });

    // Cards mucked - animate cards to muck pile
    gameController.on('cardsMucked', ({ seat }) => {
      const holeCardsView = this.holeCardsViews.get(seat);
      if (holeCardsView) {
        holeCardsView.muck();
      }
    });

    // ==========================================================================
    // Pre-Action Checkboxes
    //
    // Pre-actions show in exactly two conditions:
    //   1. New street just started and it is NOT hero's turn
    //   2. Mid-street, hero didn't fold, hero's street bet < max street bet
    // ==========================================================================

    // Refresh pre-actions when it's another player's turn
    gameEventBus.on('turn:started', ({ isHero }) => {
      if (!isHero) {
        this.updatePreActionCheckboxes();
      }
    });

    // Preflop: hero receives cards → new street, show pre-actions
    gameEventBus.on('cards:dealt:hero', () => {
      this.isNewStreet = true;
      this.updatePreActionCheckboxes();
    });

    // Any player acts → street is no longer "new", refresh pre-actions
    gameEventBus.on('player:acted', ({ seat, action }) => {
      this.isNewStreet = false;

      const heroSeat = tableState.heroSeat.get();
      if (heroSeat === null || seat === heroSeat) return;

      // Invalidate "Call $X" if bet amount changed
      if (action === 'bet' || action === 'raise' || action === 'allin') {
        this.gamePad.invalidateCallAmount();
      }

      // Refresh: opponent bet may trigger condition 2
      this.updatePreActionCheckboxes();
    });

    // Street change: new street starts, clear selection, refresh scenario
    gameEventBus.on('flop:revealed', () => { this.isNewStreet = true; this.gamePad.clearQueuedPreAction(); this.updatePreActionCheckboxes(); });
    gameEventBus.on('turn:revealed', () => { this.isNewStreet = true; this.gamePad.clearQueuedPreAction(); this.updatePreActionCheckboxes(); });
    gameEventBus.on('river:revealed', () => { this.isNewStreet = true; this.gamePad.clearQueuedPreAction(); this.updatePreActionCheckboxes(); });

    // Invalidate all on hand boundaries
    gameEventBus.on('hand:start', () => { this.isNewStreet = false; this.gamePad.invalidateAllPreActions(); });
    gameEventBus.on('hand:end', () => { this.isNewStreet = false; this.gamePad.invalidateAllPreActions(); });

    // ==========================================================================
    // Blinds Animation Setup
    // ==========================================================================
    // When blinds are posted, immediately:
    // 1. Hide player pots (so they appear when chips arrive)
    // 2. Start chip flying animation (simultaneous with stack decrease)
    gameEventBus.on('blinds:posted', ({ smallBlind, bigBlind }) => {
      // Hide pots before state subscription can show them
      this.playerPotViews.get(smallBlind.seat)?.hideForAnimation();
      this.playerPotViews.get(bigBlind.seat)?.hideForAnimation();

      // Start chip animation NOW - simultaneous with stack decrease animation
      void this.playBlindsAnimation({ smallBlind, bigBlind });

      log.game.debug`TableScreen: blinds:posted - started animation (SB seat ${smallBlind.seat}, BB seat ${bigBlind.seat})`;
    });

    // ==========================================================================
    // Bet/Call/Allin Chip Animation
    // ==========================================================================
    // When player bets/calls/raises/allins, animate chips flying to pot
    // Pattern matches blinds: hide pot → fly chips → show pot on arrival
    // @see tasks/active/T22_BET_CHIP_ANIMATION_PLAN.md
    gameEventBus.on('player:betting:start', ({ seat, amount }) => {
      const playerPotView = this.playerPotViews.get(seat);
      if (!playerPotView) return;

      // Defer display updates until chips arrive
      // For first bet: hide pot, show when chips arrive
      // For raise: keep current value visible, update when chips arrive
      if (!playerPotView.visible) {
        playerPotView.hideForAnimation();
      } else {
        playerPotView.deferDisplayUpdates();
      }

      // Play chip animation
      this.playBetChipAnimation(seat, amount);

      log.game.debug`TableScreen: player:betting:start - seat ${seat}, amount ${amount}`;
    });

    // ==========================================================================
    // GameStartSequencer Integration
    // ==========================================================================
    // Cards are dealt via GameStartSequencer which orchestrates:
    //   START-1: Reset → START-2: Blinds → START-3: Deal
    // The sequencer calls StageManager directly (no events needed).
    // Table operations are registered in registerTableOperations().
    // @see docs/systems/animations/GAME_START_ANIMATION.md
    // @see tasks/active/T35_SIMPLIFIED_PHASE5_PLAN.md

    // Ensure event collector is initialized (imported above)
    void gameStartEventCollector;

    // Legacy: Keep handStarted for dealer button animation
    // The sequencer handles reset + deal, but dealer button moves immediately on handStarted
    gameController.on('handStarted', ({ dealerSeat }) => {
      // Dealer button animation is handled by effects engine via hand:start event
      // This handler is kept for compatibility but does NOT do reset/deal anymore
      log.game.debug`TableScreen: handStarted - dealer at seat ${dealerSeat} (sequencer handles reset/deal)`;
    });

    // Deal card backs for specific opponent (animated from top-center)
    gameController.on('opponentCardsDealt', ({ seat }) => {
      const holeCardsView = this.holeCardsViews.get(seat);
      if (holeCardsView) {
        const fromPosition = this.getDealStartPosition(holeCardsView);
        holeCardsView.dealBacks(fromPosition);
      }
    });

    // Reveal opponent cards at showdown
    gameController.on('showdown', ({ playerCards }) => {
      playerCards.forEach((cards, seat) => {
        const holeCardsView = this.holeCardsViews.get(seat);
        if (holeCardsView) {
          holeCardsView.setCards(cards, true);
        }
      });
    });

    // NOTE: Winner highlighting is NOT done here.
    // It's handled by showdown:highlight event during END-3 in GameEndSequencer.
    // This ensures highlights appear at the correct time in the animation sequence.

    // Reveal cards when player shows (POKERTALK show message)
    // Hero uses show() for dramatic flourish, opponents use reveal() for flip
    gameController.on('cardsRevealed', ({ seat, cards }) => {
      const holeCardsView = this.holeCardsViews.get(seat);
      if (holeCardsView) {
        const heroSeat = tableState.heroSeat.get();
        if (seat === heroSeat) {
          // Hero showing cards - dramatic flourish (cards already face-up)
          holeCardsView.show();
        } else {
          // Opponent showing cards - flip reveal
          holeCardsView.reveal(cards);
        }
      }
    });

    // Highlight winning hand cards on community board AND player hole cards
    // Legacy handler - treats all cards as core (strong highlight)
    gameController.on('winningHand', ({ cards, handName }) => {
      // Highlight community cards (all as core - no kickers in legacy path)
      this.communityCards.highlightCards(cards, []);

      // Show hand name above community cards (e.g., "Full House")
      if (handName) {
        this.handNameView.show(handName);
      }

      // Highlight hole cards for all players if they're part of winning hand
      this.holeCardsViews.forEach((holeCardsView) => {
        holeCardsView.highlightWinningCards(cards, []);
      });
    });

    // =========================================================================
    // GAME EVENT BUS SUBSCRIPTIONS (Effects Engine integration)
    // =========================================================================

    // Player fold - animate cards to muck (works for both hero and opponents)
    gameEventBus.on('player:fold', ({ seat }) => {
      const holeCardsView = this.holeCardsViews.get(seat);
      if (holeCardsView) {
        holeCardsView.fold();
      }
    });

    // Showdown card reveal - flip opponent cards face up with animation
    gameEventBus.on('showdown:reveal', ({ seat, cards, mucked }) => {
      if (mucked) {
        // Player mucked - animate cards to muck pile (not just darken)
        const holeCardsView = this.holeCardsViews.get(seat);
        if (holeCardsView) {
          holeCardsView.muck();
        }
        return;
      }

      // Reveal cards with animation
      const holeCardsView = this.holeCardsViews.get(seat);
      if (holeCardsView && cards) {
        const isHero = seat === tableState.heroSeat.get();
        if (isHero) {
          // Hero uses flourish animation at showdown (spread + lift)
          log.effects.debug`TableScreen: showing hero cards with flourish for seat ${seat}`;
          holeCardsView.show();
        } else {
          // Opponents use standard reveal flip
          log.effects.debug`TableScreen: revealing cards for seat ${seat}`;
          holeCardsView.reveal(cards);
        }
      }
    });

    // END-3: Highlight winning cards (BOTH hole cards AND community cards)
    // This runs per-pot during the GAME_END sequence
    // @see docs/systems/animations/GAME_END_ANIMATION.md - END-3 section
    gameEventBus.on('showdown:highlight', ({ coreCards, kickerCards, handName }) => {
      const totalCards = coreCards.length + kickerCards.length;
      log.effects.info`TableScreen: showdown:highlight received with ${coreCards.length} core + ${kickerCards.length} kicker cards, handName: ${handName ?? 'none'}`;

      if (totalCards === 0) {
        log.effects.warning`TableScreen: no winning cards to highlight`;
        return;
      }

      log.effects.debug`TableScreen: END-3 highlighting ${totalCards} winning cards`;
      log.effects.debug`TableScreen: coreCards = ${JSON.stringify(coreCards)}`;
      log.effects.debug`TableScreen: kickerCards = ${JSON.stringify(kickerCards)}`;

      // Highlight community cards (board) with two-level highlighting
      this.communityCards.highlightCards(coreCards, kickerCards);

      // Show hand name above community cards (e.g., "Full House")
      if (handName) {
        this.handNameView.show(handName);
      }

      // Highlight hole cards for all players with two-level highlighting
      this.holeCardsViews.forEach((holeCardsView, seat) => {
        log.effects.debug`TableScreen: highlighting seat ${seat}`;
        holeCardsView.highlightWinningCards(coreCards, kickerCards);
      });
    });

    // Clear highlights between pots (for side pot scenarios)
    gameEventBus.on('showdown:clearHighlight', () => {
      log.effects.debug`TableScreen: clearing highlights`;
      this.communityCards.resetHighlights();
      this.handNameView.hide();
      this.holeCardsViews.forEach((holeCardsView) => {
        holeCardsView.resetHighlights();
      });
    });

    // Showdown end - final cleanup (kept for backwards compatibility)
    gameEventBus.on('showdown:end', ({ winningCards }) => {
      if (winningCards.length === 0) return;

      log.effects.debug`TableScreen: highlighting ${winningCards.length} winning cards`;

      // Highlight community cards
      this.communityCards.highlightCards(winningCards);

      // Highlight winning cards in all player's hole cards
      this.holeCardsViews.forEach((holeCardsView) => {
        holeCardsView.highlightWinningCards(winningCards);
      });
    });
  }

  /**
   * Determine which pre-action checkboxes to show based on current game state.
   *
   * Pre-actions show in exactly two conditions:
   *   1. New street just started and it is NOT hero's turn
   *   2. Mid-street, hero didn't fold, hero's street bet < max street bet
   */
  private updatePreActionCheckboxes(): void {
    const heroSeat = tableState.heroSeat.get();
    if (heroSeat === null) { this.gamePad.hidePreActions(); return; }

    // Don't show pre-actions if hero already has action buttons (their turn)
    if (tableState.heroActions.get() !== null) return;

    // Don't show if sitting out
    if (tableState.heroSitout.get()) { this.gamePad.hidePreActions(); return; }

    const heroPlayer = tableState.players.get().get(heroSeat);
    if (!heroPlayer) { this.gamePad.hidePreActions(); return; }

    // Don't show if hero is out of the hand
    if (heroPlayer.status === 'folded' || heroPlayer.status === 'allin') { this.gamePad.hidePreActions(); return; }

    // Use heroCards signal — always accurate during normal play.
    if (tableState.heroCards.get() === null) { this.gamePad.hidePreActions(); return; }

    // Determine if hero faces an uncalled bet
    let maxBetOnStreet = 0;
    tableState.players.get().forEach((player) => {
      if (player.bet > maxBetOnStreet) {
        maxBetOnStreet = player.bet;
      }
    });

    const callAmount = maxBetOnStreet - heroPlayer.bet;

    // Condition 1: new street just started, not hero's turn
    // Condition 2: mid-street, hero faces an uncalled bet (hero.bet < maxBet)
    if (this.isNewStreet || callAmount > 0) {
      if (callAmount > 0) {
        this.gamePad.showPreActions('bet', callAmount);
      } else {
        this.gamePad.showPreActions('check');
      }
    } else {
      // Neither condition met — hide pre-actions
      this.gamePad.hidePreActions();
    }
  }

  private layout(): void {
    // Background — violet casino gradient (top to bottom)
    this.background.clear();
    const gradientFill = new FillGradient({
      type: 'linear',
      start: { x: 0, y: 0 },
      end: { x: 0, y: 1 },
      colorStops: [
        { offset: 0, color: 0x1a1a3e },
        { offset: 1, color: 0x06060e },
      ],
      textureSpace: 'local',
    });
    this.background.rect(0, 0, this.screenWidth, this.screenHeight);
    this.background.fill(gradientFill);

    // Table info label position (game type + blinds)
    const infoPos = this.toScreen(TABLE_INFO_POSITION.x, TABLE_INFO_POSITION.y);
    this.tableInfoView.x = infoPos.x;
    this.tableInfoView.y = infoPos.y;

    // Table pot position
    const potPos = this.toScreen(POT_POSITION.x, POT_POSITION.y);
    this.tablePotView.x = potPos.x;
    this.tablePotView.y = potPos.y;

    // Community cards position
    const cardsPos = this.toScreen(COMMUNITY_CARDS.center.x, COMMUNITY_CARDS.center.y);
    this.communityCards.x = cardsPos.x;
    this.communityCards.y = cardsPos.y;
    this.communityCards.updateLayout(this.screenWidth);

    // Hand name position (same as community cards, yOffset handled in component)
    this.handNameView.x = cardsPos.x;
    this.handNameView.y = cardsPos.y;

    // Game pad position (bottom of screen)
    this.gamePad.y = this.screenHeight - 80;

    // Update hole cards positions
    this.updateHoleCardsPositions();
  }

  private createSeats(): void {
    // Clear existing seats
    this.seatViews.forEach((seat) => {
      this.seatsLayer.removeChild(seat);
      seat.destroy();
    });
    this.seatViews.clear();

    // Clear existing player pots
    this.playerPotViews.forEach((pot) => {
      this.playerPotsLayer.removeChild(pot);
      pot.destroy();
    });
    this.playerPotViews.clear();

    // Clear existing hole cards
    this.holeCardsViews.forEach((cards) => {
      this.playersCardsLayer.removeChild(cards);
      cards.destroy();
    });
    this.holeCardsViews.clear();

    // Create seats, player pots, and hole cards for current table size
    const positions = SEAT_POSITIONS[this.tableSize];
    if (!positions) return;

    positions.forEach((pos) => {
      // Create seat view with current screen width
      const seatView = new SeatView(pos.seat);
      seatView.resize(this.screenWidth);
      seatView.onSitClick = (seat) => {
        tableController.requestSit(seat);
      };
      seatView.onAvatarClick = (_seat, player) => {
        const heroSeat = tableState.heroSeat.get();
        // If hero is seated and clicking another player → gift popup
        if (heroSeat !== null && !player.isHero) {
          this.showGiftPopup(player);
        } else {
          this.showProfilePopup(player);
        }
      };
      this.seatsLayer.addChild(seatView);
      this.seatViews.set(pos.seat, seatView);

      // Create player pot view
      const playerPotView = new PlayerPotView(pos.seat);
      playerPotView.resize(this.screenWidth);
      this.playerPotsLayer.addChild(playerPotView);
      this.playerPotViews.set(pos.seat, playerPotView);

      // Create hole cards view (hero layout for hero seat, opponent layout for others)
      // Layout mode is updated dynamically when hero seat changes
      const holeCardsView = new HoleCardsView('opponent', pos.seat);
      this.playersCardsLayer.addChild(holeCardsView);
      this.holeCardsViews.set(pos.seat, holeCardsView);
    });

    // Ensure hero cards container renders above all hole cards
    this.playersCardsLayer.addChild(this.heroView.cardsContainer);

    // Position player pots and hole cards
    this.updatePlayerPots();
    this.updateHoleCardsPositions();

    // Initialize layout editor if enabled (dev-only)
    this.initLayoutEditor();
  }

  private updateSeats(): void {
    const players = tableState.players.get();
    const heroSeat = tableState.heroSeat.get();
    const isHeroSeated = heroSeat !== null;

    // Iterate over all seats by server seat number
    for (let serverSeat = 0; serverSeat < this.tableSize; serverSeat++) {
      const seatView = this.seatViews.get(serverSeat);
      if (!seatView) continue;

      // Get rotated position (hero always at visual position 0)
      const pos = getRotatedSeatPosition(this.tableSize, serverSeat, heroSeat);
      const screenPos = this.toScreen(pos.x, pos.y);

      // Position SeatView (kept in sync even when hidden for hero seat)
      seatView.x = screenPos.x;
      seatView.y = screenPos.y;
      seatView.setHeroSeated(isHeroSeated);

      const player = players.get(serverSeat);
      seatView.setPlayer(player ?? null);

      // Also position and update HeroView for the hero seat
      if (serverSeat === heroSeat) {
        this.heroView.setCockpitPosition(
          screenPos.x, screenPos.y,
          this.screenWidth, this.screenHeight
        );
        this.heroView.setPlayer(player ?? null);
      }
    }
  }

  private updatePlayerPots(): void {
    const positions = PLAYER_POT_POSITION[this.tableSize];
    if (!positions) return;

    const heroSeat = tableState.heroSeat.get();

    // Iterate over all seats by server seat number
    for (let serverSeat = 0; serverSeat < this.tableSize; serverSeat++) {
      const playerPotView = this.playerPotViews.get(serverSeat);
      if (!playerPotView) continue;

      // Get rotated position (absolute coordinates, not offset)
      const position = getRotatedOffset(positions, this.tableSize, serverSeat, heroSeat);
      if (!position) continue;

      // Position player pot using absolute coordinates
      const screenPos = this.toScreen(position.x, position.y);
      playerPotView.x = screenPos.x;
      playerPotView.y = screenPos.y;
    }
  }

  private updateHoleCardsPositions(): void {
    const positions = HOLE_CARDS_POSITION[this.tableSize];
    if (!positions) return;

    const heroSeat = tableState.heroSeat.get();

    // Iterate over all seats by server seat number
    for (let serverSeat = 0; serverSeat < this.tableSize; serverSeat++) {
      const holeCardsView = this.holeCardsViews.get(serverSeat);
      if (!holeCardsView) continue;

      // Get rotated position (absolute coordinates, not offset)
      const position = getRotatedOffset(positions, this.tableSize, serverSeat, heroSeat);
      if (!position) continue;

      const isHero = serverSeat === heroSeat;

      if (isHero) {
        // Hero cards position is handled by setCockpitPosition() in updateSeats()
        holeCardsView.setLayoutMode('hero');
        holeCardsView.updateLayout(this.screenWidth);
        continue;
      }

      const screenPos = this.toScreen(position.x, position.y);
      holeCardsView.x = screenPos.x;
      holeCardsView.y = screenPos.y;
      holeCardsView.setLayoutMode('opponent');
      holeCardsView.updateLayout(this.screenWidth);
    }
  }

  /**
   * Activate/deactivate HeroView when hero seat changes.
   * Swaps the holeCardsViews map entry so all forEach loops include hero cards.
   */
  private activateHeroSeat(newHeroSeat: number | null): void {
    // Deactivate previous hero seat
    if (this.activeHeroSeat !== null && this.originalHeroHoleCards) {
      // Show the SeatView that was hidden for the hero
      const prevSeatView = this.seatViews.get(this.activeHeroSeat);
      if (prevSeatView) {
        prevSeatView.visible = true;
      }

      // Restore original HoleCardsView in map
      this.originalHeroHoleCards.visible = true;
      this.holeCardsViews.set(this.activeHeroSeat, this.originalHeroHoleCards);
      this.originalHeroHoleCards = null;
    }

    // Activate new hero seat
    if (newHeroSeat !== null) {
      // Hide the SeatView for hero seat (HeroView takes over)
      const heroView = this.seatViews.get(newHeroSeat);
      if (heroView) {
        heroView.visible = false;
      }

      // Swap HoleCardsView in map to HeroView's view
      const originalCards = this.holeCardsViews.get(newHeroSeat);
      if (originalCards) {
        this.originalHeroHoleCards = originalCards;
        originalCards.visible = false;
        this.holeCardsViews.set(newHeroSeat, this.heroView.getHoleCardsView());
      }
    } else {
      // Hero left — hide HeroView
      this.heroView.setPlayer(null);
    }

    this.activeHeroSeat = newHeroSeat;
  }

  private updateDealerButton(dealerSeat: number, animate = true): void {
    if (dealerSeat < 0 || dealerSeat >= this.tableSize) return;

    const heroSeat = tableState.heroSeat.get();

    // Get absolute button position (rotated based on hero seat)
    const btnPositions = DEALER_BUTTON_POSITION[this.tableSize];
    const visualIndex = getVisualPositionIndex(this.tableSize, dealerSeat, heroSeat);
    const btnPos = btnPositions[visualIndex];

    if (!btnPos) return;

    const screenPos = this.toScreen(btnPos.x, btnPos.y);

    // Animate if button is already visible and position changed
    if (animate && this.dealerButton.visible &&
        (this.dealerButton.x !== screenPos.x || this.dealerButton.y !== screenPos.y)) {
      const effect = new DealerButtonMoveEffect(
        { type: 'dealer' },
        {
          component: this.dealerButton,
          fromX: this.dealerButton.x,
          fromY: this.dealerButton.y,
          toX: screenPos.x,
          toY: screenPos.y,
          arcHeight: 30,
        },
        { duration: 500 }
      );
      effect.play();
    } else {
      // Instant positioning (initial placement or resize)
      this.dealerButton.x = screenPos.x;
      this.dealerButton.y = screenPos.y;
    }

    this.dealerButton.visible = true;
  }

  // NOTE: updateActiveSeat() REMOVED - now handled by effects pipeline
  // player:highlight/player:unhighlight effects call SeatView.setActive()

  private updateSitoutCountdowns(timers: Map<number, number>): void {
    this.seatViews.forEach((seatView, seat) => {
      const seconds = timers.get(seat);
      seatView.setSitoutCountdown(seconds ?? null);
    });

    // Also update HeroView if hero is seated
    if (this.activeHeroSeat !== null) {
      const heroSeconds = timers.get(this.activeHeroSeat);
      this.heroView.setSitoutCountdown(heroSeconds ?? null);
    }
  }

  /**
   * Register all component positions with the TargetRegistry.
   * This enables the effects system to resolve targets to screen positions.
   *
   * IMPORTANT: All positions must be in particleLayer's coordinate space
   * since that's where flying chips are rendered.
   */
  private registerEffectTargets(): void {
    // Skip registration if screen dimensions aren't set yet
    // This happens on initial construction before first resize()
    if (this.screenWidth === 0 || this.screenHeight === 0) {
      return;
    }

    // Skip if tablePotView hasn't been positioned yet
    if (this.tablePotView.x === 0 && this.tablePotView.y === 0) {
      return;
    }

    // NOTE: particleLayer is registered in constructor (doesn't need positions)

    // Calculate table pot position directly from layout values
    // tablePotView is positioned by layout(), chips are at y=-25 within it
    const tablePotChips = this.tablePotView.getMainPotChips();
    const tablePotX = this.tablePotView.x + tablePotChips.x;
    const tablePotY = this.tablePotView.y + tablePotChips.y;

    targetRegistry.registerTablePot({
      x: tablePotX,
      y: tablePotY,
      container: this.tablePotView,
    });

    // Register table center
    const centerPos = this.toScreen(0.5, 0.5);
    targetRegistry.registerTableCenter({
      x: centerPos.x,
      y: centerPos.y,
    });

    // Register community cards for flip animations
    targetRegistry.registerCommunityCards(this.communityCards);

    // Register all seats with their pot positions
    // All layers are direct children of TableScreen at (0,0), so positions are in same coordinate space
    this.seatViews.forEach((seatView, seat) => {
      const playerPotView = this.playerPotViews.get(seat);
      if (!playerPotView) return;

      // For hero seat, use HeroView's seatContainer for position calculation
      const isHero = seat === this.activeHeroSeat;
      const seatContainer = isHero ? this.heroView.seatContainer : seatView;
      const playerInfoBar = isHero
        ? this.heroView.getPlayerInfoBar()
        : seatView.getPlayerInfoBar();

      // Get seat position from betChipsContainer (inside PlayerInfoBar)
      const betChipsContainer = playerInfoBar.getBetChipsContainer();
      const seatX = seatContainer.x + playerInfoBar.x + betChipsContainer.x;
      const seatY = seatContainer.y + playerInfoBar.y + betChipsContainer.y;

      // Avatar center position (for gift targeting)
      // Avatar sprite is anchor(0.5) at y=-10 within avatarContainer(0,0) within seatContainer
      const avatarX = seatContainer.x;
      const avatarY = seatContainer.y - 10;

      // Get player pot position - include chips container offset
      const chipsContainer = playerPotView.getChips();
      const potX = playerPotView.x + chipsContainer.x;
      const potY = playerPotView.y + chipsContainer.y;

      targetRegistry.registerSeat(seat, {
        position: {
          x: seatX,
          y: seatY,
          // Always use SeatView as container — effects need its methods (setActive, etc.)
          // Position coords come from HeroView for hero seat, but container is the SeatView
          container: seatView,
        },
        avatarPosition: {
          x: avatarX,
          y: avatarY,
        },
        potPosition: {
          x: potX,
          y: potY,
          container: playerPotView,
        },
      });
    });

    log.effects.debug`TableScreen: registered ${this.seatViews.size} effect targets`;
  }

  /**
   * Resize all seat views for a new screen width
   */
  private resizeSeatViews(screenWidth: number): void {
    this.seatViews.forEach((seatView) => {
      seatView.resize(screenWidth);
    });
    this.heroView.resize(screenWidth);
    this.playerPotViews.forEach((playerPotView) => {
      playerPotView.resize(screenWidth);
    });
  }

  /**
   * Handle leave request - show warning if player is in a hand
   */
  private handleLeaveRequest(): void {
    // Check if player is in a hand (has cards)
    const heroCards = tableState.heroCards.get();
    const isInHand = heroCards !== null;

    if (isInHand) {
      // Show warning popup
      this.showLeaveWarningPopup();
    } else {
      // Leave immediately
      tableController.leave();
    }
  }

  /**
   * Show leave warning popup
   */
  private showLeaveWarningPopup(): void {
    // Remove existing popup if any
    if (this.leaveWarningPopup) {
      this.popupLayer.removeChild(this.leaveWarningPopup);
      this.leaveWarningPopup.destroy();
    }

    this.leaveWarningPopup = new LeaveWarningPopup(this.screenWidth, this.screenHeight);

    this.leaveWarningPopup.onLeave = () => {
      tableController.leave();
      this.leaveWarningPopup = null;
    };

    this.leaveWarningPopup.onCancel = () => {
      this.leaveWarningPopup = null;
    };

    this.popupLayer.addChild(this.leaveWarningPopup);
  }

  /**
   * Handle sitout request - show warning if player is in a hand
   */
  private handleSitoutRequest(): void {
    // Check if player is in a hand (has cards)
    const heroCards = tableState.heroCards.get();
    const isInHand = heroCards !== null;

    if (isInHand) {
      // Show warning popup
      this.showSitoutWarningPopup();
    } else {
      // Sit out immediately
      tableController.requestSitout();
    }
  }

  /**
   * Show sitout warning popup
   */
  private showSitoutWarningPopup(): void {
    // Remove existing popup if any
    if (this.sitoutWarningPopup) {
      this.popupLayer.removeChild(this.sitoutWarningPopup);
      this.sitoutWarningPopup.destroy();
    }

    this.sitoutWarningPopup = new SitoutWarningPopup(this.screenWidth, this.screenHeight);

    this.sitoutWarningPopup.onSitout = () => {
      tableController.requestSitout();
      this.sitoutWarningPopup = null;
    };

    this.sitoutWarningPopup.onCancel = () => {
      this.sitoutWarningPopup = null;
    };

    this.popupLayer.addChild(this.sitoutWarningPopup);
  }

  private showBuyInPopup(seat: number, minBuyIn: number, maxBuyIn: number): void {
    // If we have a pending buy-in amount from lobby, use jumpin without seat (server picks)
    if (tableController.pendingBuyInAmount !== undefined) {
      const amount = Math.max(minBuyIn, Math.min(maxBuyIn, tableController.pendingBuyInAmount));
      tableController.jumpIn(amount);
      return;
    }

    // Remove existing popup if any
    if (this.buyInPopup) {
      this.popupLayer.removeChild(this.buyInPopup);
      this.buyInPopup.destroy();
    }

    const stakes = tableState.stakes.get();

    // Create popup immediately (in loading state with controls disabled)
    this.buyInPopup = new BuyInPopup(this.screenWidth, this.screenHeight, {
      seat,
      minBuyIn,
      maxBuyIn,
      stakes,
    });

    this.buyInPopup.onConfirm = (amount, autoRefill, autoRebuy) => {
      tableController.confirmSit(amount, autoRefill, autoRebuy);
      this.buyInPopup = null;
    };

    this.buyInPopup.onCancel = () => {
      tableController.cancelSit();
      this.buyInPopup = null;
    };

    this.buyInPopup.onInsufficientChips = (minRequired, playerChips) => {
      // Close popup and show error
      if (this.buyInPopup) {
        this.popupLayer.removeChild(this.buyInPopup);
        this.buyInPopup.destroy();
        this.buyInPopup = null;
      }
      this.showBuyinErrorPopup(`Insufficient chips. You need at least $${minRequired} but only have $${playerChips}.`);
      tableController.cancelSit();
    };

    this.popupLayer.addChild(this.buyInPopup);

    // Track when popup was shown for minimum loading time
    const popupShownAt = Date.now();
    const minLoadingTime = 500; // Minimum time to show loading state (ms)

    // Helper to apply balance with minimum delay
    const applyBalance = (chips: number) => {
      const elapsed = Date.now() - popupShownAt;
      const remainingDelay = Math.max(0, minLoadingTime - elapsed);

      setTimeout(() => {
        if (this.buyInPopup && this.buyInPopup.isLoading()) {
          this.buyInPopup.setBalance(chips);
        }
      }, remainingDelay);
    };

    // Set up listener BEFORE requesting balance (to not miss fast responses)
    const onBalanceUpdated = (state: { chips: number }) => {
      accountService.off('balanceUpdated', onBalanceUpdated);
      applyBalance(state.chips);
    };

    accountService.on('balanceUpdated', onBalanceUpdated);

    // Request fresh balance from server
    accountService.requestBalance();

    // Timeout fallback - use cached state if server doesn't respond in 3 seconds
    setTimeout(() => {
      accountService.off('balanceUpdated', onBalanceUpdated);
      if (this.buyInPopup && this.buyInPopup.isLoading()) {
        const cachedState = accountService.getLastState();
        const playerChips = cachedState?.chips ?? Infinity;
        applyBalance(playerChips);
      }
    }, 3000);
  }

  private showBuyinErrorPopup(errorMessage: string): void {
    // Remove existing popup if any
    if (this.buyinErrorPopup) {
      this.popupLayer.removeChild(this.buyinErrorPopup);
      this.buyinErrorPopup.destroy();
    }

    this.buyinErrorPopup = new BuyinErrorPopup(
      this.screenWidth,
      this.screenHeight,
      errorMessage
    );

    this.buyinErrorPopup.onDismiss = () => {
      this.buyinErrorPopup = null;
    };

    this.popupLayer.addChild(this.buyinErrorPopup);
  }

  /**
   * Show profile popup for a player.
   * Triggered by clicking a player's avatar at the table.
   */
  private showProfilePopup(player: PlayerState): void {
    // Remove existing popup if any
    if (this.profilePopup) {
      this.popupLayer.removeChild(this.profilePopup);
      this.profilePopup.destroy();
    }

    this.profilePopup = new ProfilePopup(
      this.screenWidth,
      this.screenHeight,
      player
    );

    this.profilePopup.onDismiss = () => {
      this.profilePopup = null;
    };

    this.popupLayer.addChild(this.profilePopup);
  }

  /**
   * Show gift selection popup for a target player.
   * Triggered by clicking an opponent's avatar when hero is seated.
   */
  private showGiftPopup(player: PlayerState): void {
    // Remove existing popup if any
    if (this.giftPopup) {
      this.popupLayer.removeChild(this.giftPopup);
      this.giftPopup.destroy();
    }

    this.giftPopup = new GiftPopup(
      this.screenWidth,
      this.screenHeight,
      player.profile.name,
    );

    this.giftPopup.onGiftSelected = (giftId: string) => {
      giftService.sendGift(player.seat, giftId);
    };

    this.giftPopup.onDismiss = () => {
      this.giftPopup = null;
    };

    this.popupLayer.addChild(this.giftPopup);
  }

  /**
   * Show a lightweight error toast in particleLayer.
   * Auto-dismisses after 3 seconds with fade-in/out animation.
   */
  private showErrorToast(message: string): void {
    const DURATION = 3000;
    const FONT_SIZE = 14;
    const PADDING_X = 14;
    const PADDING_Y = 8;
    const CORNER_RADIUS = 8;
    const BG_COLOR = 0x1a1a2e;
    const BG_ALPHA = 0.9;
    const TEXT_COLOR = 0xff6b6b;
    const FLOAT_DISTANCE = 12;

    const toast = new Container();
    toast.x = this.screenWidth / 2;
    toast.y = this.screenHeight * 0.15;
    toast.alpha = 0;

    const text = new Text({
      text: `⚠ ${message}`,
      style: {
        fontSize: FONT_SIZE,
        fill: TEXT_COLOR,
        fontFamily: 'Roboto, Arial, sans-serif',
        fontWeight: 'bold',
      },
    });
    text.anchor.set(0.5);

    const bgWidth = text.width + PADDING_X * 2;
    const bgHeight = text.height + PADDING_Y * 2;
    const background = new Graphics();
    background.roundRect(-bgWidth / 2, -bgHeight / 2, bgWidth, bgHeight, CORNER_RADIUS);
    background.fill({ color: BG_COLOR, alpha: BG_ALPHA });
    background.stroke({ width: 1, color: 0xff6b6b, alpha: 0.4 });

    toast.addChild(background);
    toast.addChild(text);
    this.particleLayer.addChild(toast);

    const startY = toast.y;
    const startTime = performance.now();

    const animate = (): void => {
      const elapsed = performance.now() - startTime;
      const progress = Math.min(elapsed / DURATION, 1);

      // Phase 1: Fade in (0–10%)
      if (progress < 0.1) {
        toast.alpha = progress / 0.1;
      }
      // Phase 2: Hold (10–80%)
      else if (progress < 0.8) {
        toast.alpha = 1;
      }
      // Phase 3: Fade out (80–100%)
      else {
        toast.alpha = 1 - (progress - 0.8) / 0.2;
      }

      // Gentle float upward
      toast.y = startY - FLOAT_DISTANCE * progress;

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        this.particleLayer.removeChild(toast);
        toast.destroy({ children: true });
      }
    };

    requestAnimationFrame(animate);
  }

  /**
   * Show my profile popup (hero's own profile).
   * Triggered by clicking avatar/name in RoomBar.
   */
  private showMyProfilePopup(): void {
    // Remove existing popup if any
    if (this.myProfilePopup) {
      this.popupLayer.removeChild(this.myProfilePopup);
      this.myProfilePopup.destroy();
    }

    this.myProfilePopup = new MyProfilePopup(
      this.screenWidth,
      this.screenHeight
    );

    this.myProfilePopup.onDismiss = () => {
      this.myProfilePopup = null;
    };

    this.popupLayer.addChild(this.myProfilePopup);
  }

  private showRebuyPopup(
    minBuyIn: number,
    maxBuyIn: number,
    timeout: number,
    at: 'end' | 'start'
  ): void {
    // Remove existing popup if any
    if (this.rebuyPopup) {
      this.popupLayer.removeChild(this.rebuyPopup);
      this.rebuyPopup.destroy();
    }

    const stakes = tableState.stakes.get();

    // Create popup immediately (in loading state with controls disabled)
    this.rebuyPopup = new RebuyPopup(this.screenWidth, this.screenHeight, {
      minBuyIn,
      maxBuyIn,
      timeout,
      at,
      stakes,
    });

    this.rebuyPopup.onConfirm = (amount, autoRefill, autoRebuy) => {
      tableController.sendRebuy(amount, autoRefill, autoRebuy);
      this.rebuyPopup = null;
    };

    this.rebuyPopup.onLeave = () => {
      tableController.leave();
      this.rebuyPopup = null;
    };

    this.rebuyPopup.onTimeout = () => {
      // Server will send standup message on timeout
      // Just close the popup and wait for server response
      this.rebuyPopup = null;
    };

    this.rebuyPopup.onInsufficientChips = (minRequired, playerChips) => {
      // Close popup and show error
      if (this.rebuyPopup) {
        this.popupLayer.removeChild(this.rebuyPopup);
        this.rebuyPopup.destroy();
        this.rebuyPopup = null;
      }
      this.showBuyinErrorPopup(`Insufficient chips. You need at least ${minRequired} but only have ${playerChips}.`);
    };

    this.popupLayer.addChild(this.rebuyPopup);

    // Track when popup was shown for minimum loading time
    const popupShownAt = Date.now();
    const minLoadingTime = 500;

    const applyBalance = (chips: number) => {
      const elapsed = Date.now() - popupShownAt;
      const remainingDelay = Math.max(0, minLoadingTime - elapsed);
      setTimeout(() => {
        if (this.rebuyPopup && this.rebuyPopup.isLoading()) {
          this.rebuyPopup.setBalance(chips);
        }
      }, remainingDelay);
    };

    // Set up listener BEFORE requesting balance
    const onBalanceUpdated = (state: { chips: number }) => {
      accountService.off('balanceUpdated', onBalanceUpdated);
      applyBalance(state.chips);
    };

    accountService.on('balanceUpdated', onBalanceUpdated);

    // Request fresh balance from server
    accountService.requestBalance();

    // Timeout fallback (3s) - use cached state if server doesn't respond
    setTimeout(() => {
      accountService.off('balanceUpdated', onBalanceUpdated);
      if (this.rebuyPopup && this.rebuyPopup.isLoading()) {
        const cachedState = accountService.getLastState();
        const playerChips = cachedState?.chips ?? Infinity;
        applyBalance(playerChips);
      }
    }, 3000);
  }

  private toScreen(normalizedX: number, normalizedY: number): { x: number; y: number } {
    return {
      x: normalizedX * this.screenWidth,
      y: normalizedY * this.screenHeight,
    };
  }

  // ===========================================================================
  // BLIND ANIMATION (START-2)
  // ===========================================================================

  /**
   * Play blinds animation - chips fly from seats to player pots.
   * Called by GameStartSequencer during START-2 phase.
   *
   * @see docs/systems/animations/CHIP_ANIMATION_SEAT_PLAYERPOT.md
   * @see docs/systems/animations/GAME_START_ANIMATION.md - START-2 phase
   */
  private async playBlindsAnimation(
    blinds: SequencerBlindsEvent['blinds']
  ): Promise<void> {
    const particleLayer = targetRegistry.getParticleLayer();
    if (!particleLayer) {
      log.game.warning`TableScreen: playBlindsAnimation - no particle layer`;
      return;
    }

    // NOTE: Pots are already hidden by the 'blinds:posted' handler (above).
    // That handler runs immediately when GameController emits the event,
    // BEFORE the state subscription can show the pots.

    // Play SB animation (starts immediately)
    const sbPromise = this.playBlindChipAnimation(
      blinds.smallBlind.seat,
      blinds.smallBlind.amount
    );

    // Stagger BB animation by configured delay
    await new Promise((resolve) =>
      setTimeout(resolve, BLIND_ANIMATION_TIMING.blindStagger)
    );

    // Play BB animation
    const bbPromise = this.playBlindChipAnimation(
      blinds.bigBlind.seat,
      blinds.bigBlind.amount
    );

    // Wait for both to complete
    await Promise.all([sbPromise, bbPromise]);

    log.game.debug`TableScreen: blinds animation complete`;
  }

  /**
   * Play chip animation for a single blind (SB or BB).
   * Chips fly from seat position to player pot position.
   */
  private playBlindChipAnimation(seat: number, amount: number): Promise<void> {
    const seatReg = targetRegistry.getSeat(seat);
    const particleLayer = targetRegistry.getParticleLayer();
    const playerPotView = this.playerPotViews.get(seat);

    // Fallback: show pot immediately if we can't animate
    if (!seatReg || !particleLayer) {
      log.game.warning`TableScreen: playBlindChipAnimation - missing seatReg or particleLayer for seat ${seat}`;
      playerPotView?.showAfterAnimation();
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      const effect = new ChipBetEffect(
        { type: 'seat', id: seat },
        {
          container: particleLayer,
          fromX: seatReg.position.x,
          fromY: seatReg.position.y,
          toX: seatReg.potPosition.x,
          toY: seatReg.potPosition.y,
          amount,
          onComplete: () => {
            // Show pot when chips "arrive"
            playerPotView?.showAfterAnimation();
            resolve();
          },
        },
        { duration: BLIND_ANIMATION_TIMING.flightDuration }
      );
      effect.play();
    });
  }

  /**
   * Play chip animation for a bet/call/raise/allin action.
   * Chips fly from seat position to player pot position.
   *
   * @see docs/systems/animations/CHIP_ANIMATION_SEAT_PLAYERPOT.md
   * @see tasks/active/T22_BET_CHIP_ANIMATION_PLAN.md
   */
  private playBetChipAnimation(seat: number, amount: number): void {
    const seatReg = targetRegistry.getSeat(seat);
    const particleLayer = targetRegistry.getParticleLayer();
    const playerPotView = this.playerPotViews.get(seat);

    // Fallback: show pot immediately if we can't animate
    if (!seatReg || !particleLayer) {
      log.game.warning`TableScreen: playBetChipAnimation - missing seatReg or particleLayer for seat ${seat}`;
      playerPotView?.showAfterAnimation();
      return;
    }

    const effect = new ChipBetEffect(
      { type: 'seat', id: seat },
      {
        container: particleLayer,
        fromX: seatReg.position.x,
        fromY: seatReg.position.y,
        toX: seatReg.potPosition.x,
        toY: seatReg.potPosition.y,
        amount,
        onComplete: () => {
          // Show pot when chips "arrive"
          playerPotView?.showAfterAnimation();
        },
      },
      { duration: STREET_END_TIMING.betToPlayerPot }
    );
    effect.play();
  }

  // ===========================================================================
  // DEALING ANIMATION (START-3)
  // ===========================================================================

  /**
   * Start round-robin dealing sequence.
   * Deals cards clockwise from SB, one card at a time, N rounds
   * (2 for Hold'em, 4 for Omaha).
   *
   * @see docs/systems/animations/GAME_START_ANIMATION.md - Rules and specs
   * @see tasks/active/T18_ROUND_ROBIN_DEALING_IMPL.md - Implementation
   */
  private startDealingSequence(dealerSeat: number): void {
    const players = tableState.players.get();
    const heroSeat = tableState.heroSeat.get();
    const maxSeats = tableState.tableSize.get();
    const holeCardCount = tableState.holeCardCount.get();

    // Get active seats sorted clockwise from dealer+1 (SB position)
    const activeSeats: number[] = [];
    players.forEach((_, seat) => activeSeats.push(seat));

    activeSeats.sort((a, b) => {
      const aOffset = (a - dealerSeat - 1 + maxSeats) % maxSeats;
      const bOffset = (b - dealerSeat - 1 + maxSeats) % maxSeats;
      return aOffset - bOffset;
    });

    // Ensure all HoleCardsViews have the correct card slot count
    for (const seat of activeSeats) {
      this.holeCardsViews.get(seat)?.setCardCount(holeCardCount);
    }

    const { cardStaggerDelay, roundGapDelay } = DEALING_CONFIG;
    const playerCount = activeSeats.length;

    // Deal N rounds (one card per player per round)
    for (let round = 0; round < holeCardCount; round++) {
      const roundStart = round * (playerCount * cardStaggerDelay + roundGapDelay);
      activeSeats.forEach((seat, index) => {
        const delay = roundStart + index * cardStaggerDelay;
        setTimeout(() => {
          this.dealCardToSeat(seat, round, seat === heroSeat);
        }, delay);
      });
    }
  }

  /**
   * Deal a single card to a seat during round-robin dealing.
   */
  private dealCardToSeat(seat: number, cardIndex: number, isHero: boolean): void {
    const holeCardsView = this.holeCardsViews.get(seat);
    if (!holeCardsView) return;

    const fromPosition = this.getDealStartPosition(holeCardsView);

    if (isHero) {
      // Hero cards - get from tableState (always available at this point)
      const heroCards = tableState.heroCards.get();
      const card = heroCards?.[cardIndex];
      if (card) {
        holeCardsView.dealSingleCard(cardIndex, card, fromPosition);
      }
    } else {
      // Opponent cards - deal face-down backs
      holeCardsView.dealSingleCardBack(cardIndex, fromPosition);
    }

    // Play deal sound
    void audioServiceAdapter.play('deal');
  }

  /**
   * Get the deal start position (top-center of screen) in a HoleCardsView's local coordinates.
   * Cards will fly from this position to their final position.
   */
  /**
   * DEBUG: Trigger a random mock gift between two occupied seats.
   * Activated via Shift+G keyboard shortcut.
   */
  private triggerDebugGift(): void {
    const players = tableState.players.get();
    const occupiedSeats = [...players.keys()];

    if (occupiedSeats.length < 2) {
      log.gifts.warning`DEBUG: Need at least 2 players for a test gift`;
      return;
    }

    // Pick random sender and receiver
    const senderIdx = Math.floor(Math.random() * occupiedSeats.length);
    const senderSeat = occupiedSeats[senderIdx]!;
    let receiverIdx = Math.floor(Math.random() * (occupiedSeats.length - 1));
    if (receiverIdx >= senderIdx) receiverIdx++;
    const receiverSeat = occupiedSeats[receiverIdx]!;

    // Pick a random gift from catalog
    const randomGift = GIFT_CATALOG[Math.floor(Math.random() * GIFT_CATALOG.length)];
    if (!randomGift) return;

    log.gifts.info`DEBUG: Gift ${randomGift.id} from seat ${String(senderSeat)} → seat ${String(receiverSeat)}`;

    giftService.triggerMockGift(senderSeat, receiverSeat, randomGift.id);
  }

  private getDealStartPosition(holeCardsView: HoleCardsView): { x: number; y: number } {
    // Screen top-center in global coordinates
    const topCenterGlobal = new Point(this.screenWidth / 2, 0);

    // Convert to HoleCardsView's local coordinates
    const localPos = holeCardsView.toLocal(topCenterGlobal, this);

    return { x: localPos.x, y: localPos.y };
  }
}

/**
 * Factory function to create TableScreen
 */
export function createTableScreen(width: number, height: number): TableScreen {
  return new TableScreen(width, height);
}
